import { ChangeDetectionStrategy, Component, booleanAttribute, EventEmitter, Input, Output } from '@angular/core';
import { NgClass, NgTemplateOutlet } from '@angular/common';
import { TranslatePipe } from '@ngx-translate/core';

/** Les Willy d'état : l'image dit l'émotion juste. */
export type EmptyStateWilly = 'veggies' | 'hello' | 'hi' | 'dontknow' | 'watering' | 'writing';

export const WILLY_SRC: Record<EmptyStateWilly, string> = {
  veggies: '/assets/images/willy/willy_veggies.png',
  hello: '/assets/images/willy/willy_hello.png',
  hi: '/assets/images/willy/willy_hi.webp',
  dontknow: '/assets/images/willy/willy_dontknow.png',
  watering: '/assets/images/willy/willy_watering.png',
  writing: '/assets/images/willy/willy_writing.png',
};

/**
 * État vide d'un écran : la réponse est arrivée, il n'y a rien à montrer.
 * Le pendant de `vw-error-state` (« ça n'a pas chargé »), avec les mêmes
 * entrées : titre, message, libellé d'action, variante compacte.
 *
 * Willy dans sa pastille (rien à faire : panier ; rien trouvé : « je ne sais
 * pas » ; c'est fait : salut), ou une icône projetée avec l'attribut `icon`,
 * puis un titre, une phrase qui dit ce qui arrivera ici, et une sortie.
 *
 * Le bouton peut porter un pictogramme, projeté avec `emptyStateActionIcon`
 * (le « + » d'un ajout).
 *
 * Usage :
 * ```html
 * <vw-empty-state willy="veggies" titleKey="…" messageKey="…" actionKey="…" (action)="…" />
 * <vw-empty-state titleKey="…" messageKey="…"><vw-bell-icon icon /></vw-empty-state>
 * ```
 */
@Component({
  selector: 'vw-empty-state',
  standalone: true,
  imports: [NgClass, NgTemplateOutlet, TranslatePipe],
  templateUrl: './empty-state.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class EmptyStateComponent {
  /** Titre (clé i18n) ; `null` pour ne garder que le message. */
  @Input() titleKey: string | null = null;
  /** Paramètres du titre. */
  @Input() titleParams: Record<string, unknown> | undefined;
  /** Message (clé i18n) ; `null` sans message. */
  @Input() messageKey: string | null = null;
  /** Paramètres du message. */
  @Input() messageParams: Record<string, unknown> | undefined;
  /** Sans illustration ni grandes marges, pour s'insérer dans une étape ou un bloc. */
  @Input({ transform: booleanAttribute }) compact = false;
  /** Libellé du bouton (clé i18n) ; sans libellé, pas de bouton. Le bouton émet `action`. */
  @Input() actionKey: string | null = null;
  /** Bouton plein (geste attendu) ou contour (sortie discrète, par défaut). */
  @Input() actionLook: 'primary' | 'secondary' = 'secondary';
  /** Seconde sortie, en lien texte sous le bouton. Émet `secondaryAction`. */
  @Input() secondaryActionKey: string | null = null;
  /** Geste en cours (envoi, chargement) : les boutons se désactivent. */
  @Input() busy = false;
  /** Willy à montrer ; sans Willy, l'icône projetée (attribut `icon`) prend la pastille. */
  @Input() willy: EmptyStateWilly | null = null;

  /**
   * `start` : aligné à gauche dans une carte (premier lancement de l'accueil),
   * Willy petit en haut à droite, une pastille projetée avec `emptyStateKicker`
   * au-dessus du titre, et les deux boutons côte à côte.
   */
  @Input() align: 'center' | 'start' = 'center';
  @Output() action = new EventEmitter<void>();
  @Output() secondaryAction = new EventEmitter<void>();

  get willySrc(): string | null {
    return this.willy ? WILLY_SRC[this.willy] : null;
  }

  onAction(): void {
    if (!this.busy) this.action.emit();
  }

  onSecondaryAction(): void {
    if (!this.busy) this.secondaryAction.emit();
  }
}
